import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  useStudyGroupResults,
  useStudyGroup,
  useCreateStudyGroup,
} from '../lib/api';

export function StudyGroupResultsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading } = useStudyGroupResults(id!);
  const { data: groupData } = useStudyGroup(id!);
  const playAgain = useCreateStudyGroup();

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const handlePlayAgain = async () => {
    if (!groupData?.group) return;
    const g = groupData.group;
    try {
      const result = await playAgain.mutateAsync({
        sourceType: g.source_type,
        sourceId: g.source_id,
        topicFilter: g.topic_filter ?? null,
        questionCount: g.question_count,
        timerSeconds: g.timer_seconds,
      });
      navigate(`/study-groups/${result.group.id}/lobby`);
    } catch { /* error handled */ }
  };

  if (isLoading || !data) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] text-slate-500">
        Loading results...
      </div>
    );
  }

  const standings = data.standings ?? [];

  return (
    <div className="max-w-lg mx-auto pb-24 px-4 space-y-5">
      {/* ── Header ────────────────────────────────────────────── */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/study-groups')}
          className="text-slate-500 hover:text-squishy-dark transition-colors"
          aria-label="Back"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div>
          <h1 className="text-xl font-bold text-squishy-dark">Results</h1>
          <p className="text-xs text-slate-500">
            {groupData?.group?.title ?? ''} · {data.totalQuestions} questions
          </p>
        </div>
      </div>

      {/* ── Leaderboard ───────────────────────────────────────── */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100">
          <h2 className="text-sm font-semibold text-squishy-dark">Final Standings</h2>
        </div>
        <div className="divide-y divide-slate-100">
          {standings.map((p) => (
            <div
              key={p.userId}
              className={`flex items-center gap-3 px-4 py-3 ${p.status === 'left' ? 'opacity-60' : ''}`}
            >
              {/* Rank */}
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                p.rank === 1 ? 'bg-squishy-accent text-squishy-dark' :
                p.rank === 2 ? 'bg-slate-300 text-slate-700' :
                p.rank === 3 ? 'bg-amber-600/20 text-amber-700' :
                'bg-slate-100 text-slate-500'
              }`}>
                {p.rank}
              </div>

              {/* Avatar + name */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-slate-800 truncate">
                    {p.displayName}
                  </span>
                  {p.status === 'left' && (
                    <span className="text-xs text-slate-400">(disconnected)</span>
                  )}
                </div>
                <div className="text-xs text-slate-400">
                  {p.accuracy}% accuracy
                  {p.avgResponseMs ? ` · ${(p.avgResponseMs / 1000).toFixed(1)}s avg` : ''}
                  {p.longestStreak > 1 ? ` · ${p.longestStreak} streak` : ''}
                </div>
              </div>

              {/* Points */}
              <div className="text-right flex-shrink-0">
                <div className="text-sm font-bold text-squishy-dark">{p.totalPoints}</div>
                <div className="text-xs text-slate-400">
                  {p.correctCount}/{data.totalQuestions}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── MVP Awards ────────────────────────────────────────── */}
      {data.awards && Object.keys(data.awards).length > 0 && (
        <div className="space-y-2">
          <h2 className="text-sm font-semibold text-squishy-dark">Awards</h2>
          <div className="grid grid-cols-2 gap-2">
            {data.awards.fastestFingers && (
              <AwardCard
                icon="\u26A1"
                label="Fastest Fingers"
                name={data.awards.fastestFingers.displayName}
                value={`${(data.awards.fastestFingers.avgMs / 1000).toFixed(1)}s avg`}
              />
            )}
            {data.awards.streakKing && (
              <AwardCard
                icon="\uD83D\uDD25"
                label="Streak King"
                name={data.awards.streakKing.displayName}
                value={`${data.awards.streakKing.streak} in a row`}
              />
            )}
            {data.awards.clutchPlayer && (
              <AwardCard
                icon="\uD83C\uDFAF"
                label="Clutch Player"
                name={data.awards.clutchPlayer.displayName}
                value={`${data.awards.clutchPlayer.clutchPoints} pts (last 5)`}
              />
            )}
            {data.awards.perfectScore && Array.isArray(data.awards.perfectScore) && (
              data.awards.perfectScore.map((p: any) => (
                <AwardCard
                  key={p.userId}
                  icon="\uD83C\uDFC6"
                  label="Perfect Score"
                  name={p.displayName}
                  value="100%"
                />
              ))
            )}
          </div>
        </div>
      )}

      {/* ── Topic breakdown ───────────────────────────────────── */}
      {data.topicBreakdown.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
          <h2 className="text-sm font-semibold text-squishy-dark">Topic Breakdown</h2>
          <div className="space-y-3">
            {data.topicBreakdown.map((t) => (
              <div key={t.topic}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-slate-700 font-medium">{t.topic}</span>
                  <span className="text-xs text-slate-400">
                    You: {t.userAccuracy}% / Group: {t.groupAccuracy}%
                  </span>
                </div>
                <div className="flex gap-1 h-2">
                  <div className="flex-1 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-squishy-primary rounded-full transition-all"
                      style={{ width: `${t.userAccuracy}%` }}
                    />
                  </div>
                  <div className="flex-1 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-slate-400 rounded-full transition-all"
                      style={{ width: `${t.groupAccuracy}%` }}
                    />
                  </div>
                </div>
                <div className="flex gap-1 mt-0.5">
                  <span className="flex-1 text-[10px] text-squishy-primary">You</span>
                  <span className="flex-1 text-[10px] text-slate-400">Group</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Action buttons ────────────────────────────────────── */}
      <div className="flex gap-3">
        <button
          onClick={() => navigate(`/study-groups/${id}/review`)}
          className="flex-1 bg-white border border-slate-200 text-squishy-dark font-semibold py-3 rounded-xl hover:border-squishy-primary transition-colors text-sm"
        >
          Review Questions
        </button>
        <button
          onClick={handlePlayAgain}
          disabled={playAgain.isPending}
          className="flex-1 bg-squishy-primary hover:bg-teal-600 text-white font-semibold py-3 rounded-xl disabled:opacity-50 transition-all text-sm"
        >
          {playAgain.isPending ? 'Creating...' : 'Play Again'}
        </button>
      </div>
    </div>
  );
}

function AwardCard({ icon, label, name, value }: { icon: string; label: string; name: string; value: string }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3 text-center">
      <div className="text-lg mb-1">{icon}</div>
      <div className="text-xs font-semibold text-squishy-dark">{label}</div>
      <div className="text-xs text-slate-500 mt-0.5">{name}</div>
      <div className="text-xs text-squishy-primary font-medium mt-0.5">{value}</div>
    </div>
  );
}
